import { Component, computed, effect, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { ELanguage } from '@app/app.interface';
import { INews } from '@core/interfaces/news.interface';
import { AuthService } from '@core/services/auth.service';
import { IconComponent } from '@shared/components/icon/icon.component';
import { NewsDialogComponent } from '@shared/components/news-dialog/news-dialog.component';
import { INewsDialogData } from '@shared/components/news-dialog/news-dialog.interface';
import { NgxSkeletonLoaderModule } from 'ngx-skeleton-loader';
import { distinctUntilChanged, EMPTY, interval, map, startWith, switchMap } from 'rxjs';
import { NotificationsButtonService } from './notifications-button.service';
import { NotificationsButtonStore } from './notifications-button.store';
import { NotificationsPanelComponent } from './notifications-panel/notifications-panel.component';

// Background refresh for the globally-mounted bell - keeps unread news current for a session that
// stays open for a long time without a full page reload.
const POLL_INTERVAL_MS = 10 * 60 * 1000;

@Component({
  imports: [NotificationsPanelComponent, IconComponent, MatButtonModule, NgxSkeletonLoaderModule],
  providers: [NotificationsButtonStore],
  selector: 'app-notifications-button',
  styles: [
    `
      :host {
        position: fixed;
        top: 24px;
        right: 24px;
        z-index: 500;
      }
    `,
  ],
  templateUrl: './notifications-button.component.html',
})
export class NotificationsButtonComponent {
  public readonly isPanelOpen = signal<boolean>(false);

  public readonly hasNewNews = computed<boolean>(() => {
    this.isPanelOpen();
    const news = this.store.news$();
    const readNewsIds = this.notificationsButtonService.getReadNewsIds();

    return news.some((item) => item.id && !readNewsIds.includes(item.id));
  });

  public readonly store = inject(NotificationsButtonStore);
  private readonly authService = inject(AuthService);
  private readonly notificationsButtonService = inject(NotificationsButtonService);
  private readonly dialog = inject(MatDialog);

  // Ensures at most one news dialog is ever open at a time, whether it was triggered by the poll's
  // auto-open or by the user clicking an item in the panel - a later attempt to open one while this
  // is set is a no-op instead of stacking another dialog on top.
  private openNewsDialogRef: MatDialogRef<NewsDialogComponent> | null = null;

  // Guards against re-triggering the auto-open for the same "latest unread" id on every effect run.
  private lastAutoOpenedNewsId: string | null = null;

  // Language of the last successfully loaded news list - lets the effect tell a language switch
  // apart from a regular poll.
  private loadedNewsLanguageCode: string | null = null;

  constructor() {
    // News follows the user's saved language (e.g. a profile save): refetch right away on a change,
    // then poll in that language. Signed out (null user) there is nothing to fetch, so polling stops.
    this.authService.currentDbUser$
      .pipe(
        map((user) => (user ? (user.language ?? ELanguage.en) : null)),
        distinctUntilChanged(),
        switchMap((languageCode) =>
          languageCode
            ? interval(POLL_INTERVAL_MS).pipe(
                startWith(0),
                map(() => languageCode),
              )
            : EMPTY,
        ),
        takeUntilDestroyed(),
      )
      .subscribe((languageCode) => this.store.fetchNews(languageCode));

    effect(() => {
      const news = this.store.news$();
      const languageCode = this.store.languageCode$();

      // Only act on a landed list - a failed fetch after a language switch must still count as
      // pending, so its retry is recognized as the switch too.
      if (this.store.loading$() || this.store.error$()) {
        return;
      }

      const isLanguageSwitch = this.loadedNewsLanguageCode !== null && languageCode !== this.loadedNewsLanguageCode;

      this.loadedNewsLanguageCode = languageCode;

      const readNewsIds = this.notificationsButtonService.getReadNewsIds();
      // news is already ordered priority DESC, start DESC by the backend - the first unread item
      // is "the latest news" to surface.
      const latestUnread = news.find((item) => item.id && !readNewsIds.includes(item.id));

      if (this.isPanelOpen()) {
        // While the panel is open the user can already see every item in it, so nothing should
        // stay "unread" - whether it was there when the panel opened or just arrived via poll.
        // Return here (not just mark as read) so a poll landing while the panel is already open
        // never falls through to the auto-open dialog below for an item the user can already see.
        this.notificationsButtonService.markNewsAsRead(news);

        return;
      }

      if (!latestUnread?.id || latestUnread.id === this.lastAutoOpenedNewsId) {
        return;
      }

      this.lastAutoOpenedNewsId = latestUnread.id;

      // News ids are per language, so after a switch everything is "unread" - including items the
      // user already read in the other language. Keep the unread dot, but don't pop anything up.
      if (isLanguageSwitch) {
        return;
      }

      this.isPanelOpen.set(true);
      this.notificationsButtonService.markNewsAsRead(news);
      this.openNewsDialog(latestUnread);
    });
  }

  public onNewsItemClick(newsItem: INews): void {
    this.openNewsDialog(newsItem);
  }

  public togglePanel(): void {
    const newState = !this.isPanelOpen();

    this.isPanelOpen.set(newState);
    // Opening or closing the panel both mean the user has seen the full list - either way nothing
    // in it should be left "unread".
    this.notificationsButtonService.markNewsAsRead(this.store.news$());
  }

  public onClosePanel(): void {
    this.isPanelOpen.set(false);
    this.notificationsButtonService.markNewsAsRead(this.store.news$());
  }

  private openNewsDialog(newsItem: INews): void {
    if (this.openNewsDialogRef) {
      return;
    }

    const data: INewsDialogData = { news: newsItem };

    this.openNewsDialogRef = this.dialog.open(NewsDialogComponent, {
      data,
      maxWidth: '800px',
      width: '90%',
    });

    this.openNewsDialogRef.afterClosed().subscribe(() => {
      this.openNewsDialogRef = null;
    });
  }
}
